import { useMemo, useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { ELLMProvider, getMastraOverrideModels, OpenRouterModelType } from "@aix/apiutils/src/llms/enums"
import type { ModelInfo } from "@aix/apiutils/src/llms/enums"
import { Button, MastraPlayground, ModelSelector, toast } from "@aix/ui"
import type { ModelOption } from "@aix/ui"

import { api } from "~/utils/api"
import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"
import { getAPIBaseUrl } from "~/lib/env"

/** Empty selection = no modelOverride sent, so the agent runs its own configured model. */
const AGENT_DEFAULT_MODEL_ID = ""
const RECOMMENDED_MODEL = OpenRouterModelType.DEEPSEEK_V4_PRO_0813

/** OpenRouter slugs carry the real vendor ("deepseek/…"); native models use their provider. */
const providerKeyFor = (model: ModelInfo): string =>
  model.provider === ELLMProvider.OPENROUTER ? (model.id.split("/")[0] ?? model.provider) : model.provider

// Same shortlist the API allowlists for modelOverride (service/mastraOverrides.ts) — one source of truth.
const OVERRIDE_MODELS: ModelOption[] = getMastraOverrideModels().map((model) => ({
  id: model.id,
  name: model.name,
  provider: model.provider === ELLMProvider.OPENROUTER ? `${providerKeyFor(model)} via OpenRouter` : model.providerName,
  providerKey: providerKeyFor(model),
  tier: model.tier,
  description: model.description,
  recommended: model.id === RECOMMENDED_MODEL,
}))

/** Refresh while chatting so edits the composition agents make show up without a reload. */
const COMPOSITION_POLL_MS = 8000

/** Summary of the video a composition-agent thread built, with a hand-off to the track editor. */
function ThreadCompositionBar({ threadId }: { threadId: string }) {
  const navigate = useNavigate()
  const { data } = api.mastraComposition.getForThread.useQuery(
    { threadId },
    { refetchInterval: COMPOSITION_POLL_MS, refetchOnWindowFocus: true },
  )
  const openInEditor = api.mastraComposition.openInEditor.useMutation({
    onSuccess: ({ videoId }) => {
      void navigate({ to: "/create/edit", search: { videoId } })
    },
    onError: (error) => toast.error(error.message),
  })
  const composition = data?.composition
  if (!composition) return null

  const latest = data.versions[0]
  const change = latest?.change
  const changeSummary = change
    ? [
        change.added.length > 0 && `+${change.added.length} clips`,
        change.removed.length > 0 && `−${change.removed.length} clips`,
        change.changed.length > 0 && `${change.changed.length} edited`,
        change.styleChanged && "style",
        change.modulesChanged.length > 0 && "overlays",
      ]
        .filter(Boolean)
        .join(", ")
    : ""

  return (
    <div className="flex items-center gap-3 border-b px-4 py-2 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">{composition.name}</span>
      <span>
        {composition.durationSec.toFixed(1)}s · {composition.width}×{composition.height} · {composition.clipCount} clips
      </span>
      {latest && (
        <span>
          v{data.versions.length} — {latest.label}
          {changeSummary && ` (${changeSummary})`}
        </span>
      )}
      <Button
        size="sm"
        variant="outline"
        className="ml-auto h-7"
        disabled={!composition.canOpenInEditor}
        loading={openInEditor.isLoading}
        title={composition.editorBlockedReason ?? "Copy this video into a new editor draft"}
        onClick={() => openInEditor.mutate({ threadId })}
      >
        Open in editor
      </Button>
    </div>
  )
}

function AdminMastraPage() {
  const utils = api.useUtils()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const urlThreadId = (search as any).threadId ?? null

  const [selectedAgent, setSelectedAgent] = useState("vidmax-assistant")
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false)

  // ── tRPC queries ──
  const { data: agents } = api.mastra.listAgents.useQuery(undefined, { refetchOnWindowFocus: false })
  const { data: agentDetails, isLoading: agentDetailsLoading } = api.mastra.getAgentDetails.useQuery(
    { agentName: selectedAgent },
    { refetchOnWindowFocus: false },
  )
  const agentModel = agentDetails?.model
  const modelOptions = useMemo<ModelOption[]>(
    () => [
      {
        id: AGENT_DEFAULT_MODEL_ID,
        name: "Agent default",
        provider: agentModel ?? "the agent's own model",
        tier: "standard",
        description: "No override — runs the model configured on the agent",
      },
      ...OVERRIDE_MODELS,
    ],
    [agentModel],
  )
  const { data: threads } = api.mastra.getThreads.useQuery(undefined, { refetchOnWindowFocus: false })
  const {
    data: threadMessages,
    isFetched: threadMessagesFetched,
    isLoading: threadMessagesLoading,
  } = api.mastra.getMessages.useQuery(
    { threadId: urlThreadId ?? "" },
    { enabled: !!urlThreadId, refetchOnWindowFocus: false },
  )

  // ── tRPC mutations ──
  const renameMutation = api.mastra.renameThread.useMutation({
    onSuccess: () => utils.mastra.getThreads.invalidate(),
  })
  const deleteMutation = api.mastra.deleteThread.useMutation({
    onSuccess: () => utils.mastra.getThreads.invalidate(),
  })

  return (
    <>
      <CustomHead title="Mastra Chat" />
      <div className="flex h-[calc(100vh-64px)] flex-col">
        {urlThreadId && <ThreadCompositionBar threadId={urlThreadId} />}
        <div className="min-h-0 flex-1">
          <MastraPlayground
            streamEndpoint={`${getAPIBaseUrl()}/api/mastra-chat/stream`}
            agents={agents}
            agentDetails={agentDetails}
            agentDetailsLoading={agentDetailsLoading}
            threads={threads}
            threadMessages={threadMessages}
            threadMessagesFetched={threadMessagesFetched}
            threadMessagesLoading={threadMessagesLoading}
            urlThreadId={urlThreadId}
            defaultAgent="vidmax-assistant"
            onAgentChange={setSelectedAgent}
            onSelectThread={(threadId) => {
              void navigate({ to: "/admin/mastra", search: { threadId } })
            }}
            onNewChat={() => {
              void navigate({ to: "/admin/mastra", search: {}, replace: true })
            }}
            onRenameThread={(threadId, title) => renameMutation.mutate({ threadId, title })}
            onDeleteThread={(threadId) => deleteMutation.mutate({ threadId })}
            onThreadsInvalidate={() => {
              void utils.mastra.getThreads.invalidate()
            }}
            renderModelSelector={({ selectedModel, onModelChange }) => (
              <ModelSelector
                models={modelOptions}
                selectedModelId={selectedModel}
                onSelectModel={onModelChange}
                isOpen={modelSelectorOpen}
                onOpenChange={setModelSelectorOpen}
                compact
                flat
              />
            )}
          />
        </div>
      </div>
    </>
  )
}

function AdminMastraPageWrapper() {
  return (
    <SuperAdminLayout>
      <AdminMastraPage />
    </SuperAdminLayout>
  )
}

export const Route = createFileRoute("/admin/mastra")({
  component: AdminMastraPageWrapper,
})
